// PRODUCTION — Flow editor "live updates" left column.
//
// Renders the live impact of the pending template changes (recomputed on every
// draft edit, debounced): per config/step change type, affected-contractor
// counts, and human-readable outcomes, plus a validation banner. Sticky, fixed
// viewport height (matches the config panel), with an internally-scrolling body
// and a pinned "View fullscreen" action. State comes from Redux; sub-components
// live in sibling files.

import React, { Fragment, memo, useState } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'

import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { getFlowEditorDraftValidation } from 'src/selectors/onboardingFlowEditor'
import {
  getTemplatePreviewData,
  getTemplatePreviewError,
  getTemplatePreviewLastUpdatedAt,
  getTemplatePreviewLoading,
} from 'src/selectors/templatePreview'
import { ConfigSection } from './ConfigSection'
import { EmptyState } from './EmptyState'
import { FlowEditorChangesModal } from './FlowEditorChangesModal'
import { LoadingState } from './LoadingState'
import { StatusIndicator } from './StatusIndicator'
import { ValidationBanner } from './ValidationBanner'

const FlowEditorLiveUpdatesPanelComponent = () => {
  const data = useSelector(getTemplatePreviewData)
  const loading = useSelector(getTemplatePreviewLoading)
  const error = useSelector(getTemplatePreviewError)
  const lastUpdatedAt = useSelector(getTemplatePreviewLastUpdatedAt)
  const validation = useSelector(getFlowEditorDraftValidation)

  const [showFullscreen, setShowFullscreen] = useState(false)

  const refreshing = loading && data !== null
  const configs = data?.configs ?? []

  const renderBody = () => {
    if (loading && data === null) {
      return <LoadingState />
    }

    if (error !== null && data === null) {
      return (
        <EmptyState
          icon="gs_triangle_exclamation"
          title="Couldn't load preview"
          subtitle="It will retry on your next change."
        />
      )
    }

    if (configs.length === 0) {
      // An invalid draft (e.g. an empty group) produces no diff actions, so the
      // preview clears to zero configs. The validation banner above already
      // explains why — don't contradict it with "No pending changes".
      if (!validation.isValid) {
        return null
      }

      return (
        <EmptyState
          icon="gs_eye_open"
          title="No pending changes"
          subtitle="Edit a step or group to see who it will affect — updated live."
        />
      )
    }

    return (
      <div css={tw`flex flex-col gap-4`}>
        {configs.map((config) => {
          return <ConfigSection key={config.configLabel} config={config} />
        })}
      </div>
    )
  }

  return (
    <Fragment>
      <div
        css={tw`flex min-h-0 w-80 flex-shrink-0 flex-col overflow-hidden rounded-xl border border-shades-200 bg-shades-50 border-solid`}
      >
        <div
          css={tw`box-border flex min-h-[61px] flex-shrink-0 items-center justify-between border-0 border-b border-solid border-shades-100 bg-shades-0 py-3.5 pl-4 pr-3`}
        >
          <div css={tw`flex items-center gap-2`}>
            <GSIcon iconName="gs_zap" width={20} height={16} css={tw`fill-primary-500`} />
            <span css={tw`text-[15px] font-semibold text-shades-900`}>Live updates</span>
          </div>
          <StatusIndicator refreshing={refreshing} lastUpdatedAt={lastUpdatedAt} />
        </div>

        <div css={tw`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3`}>
          {!validation.isValid && <ValidationBanner validation={validation} />}
          {renderBody()}
        </div>

        <div
          css={tw`flex-shrink-0 border-0 border-t border-solid border-shades-100 bg-shades-0 p-3`}
        >
          <Button
            variant="outlined"
            size="small"
            color="white"
            onClick={() => setShowFullscreen(true)}
            leftIcon={
              <GSIcon
                iconName="gs_square_arrow_top_right"
                width={14}
                height={14}
                css={tw`fill-shades-600`}
              />
            }
            className={tw`!w-full !border-shades-200 !text-shades-700 hover:!bg-shades-50`}
          >
            View fullscreen
          </Button>
        </div>
      </div>

      <FlowEditorChangesModal
        open={showFullscreen}
        mode="view"
        onClose={() => setShowFullscreen(false)}
      />
    </Fragment>
  )
}

export const FlowEditorLiveUpdatesPanel = memo(FlowEditorLiveUpdatesPanelComponent)

export default FlowEditorLiveUpdatesPanel
